import type React from 'react'
import {useState, useEffect} from 'react'
import {SidePanel} from '@/components/GDS/organisms/SidePanel'
import KPIField from '@/components/common/KPIField'
import type {BenchmarkMetadata} from '@/services/program/programService'
import {
  GLOBAL_KPI_ORDERED_FIELDS,
  GLOBAL_KPI_FIELD_TYPE,
  GLOBAL_KPI_PERCENT_MIN,
  GLOBAL_KPI_PERCENT_MAX,
  GLOBAL_KPI_PERCENT_RANGE_MESSAGE,
  TWO_DECIMAL_PATTERN,
} from '../constants'
import Button from '@/components/GDS/molecules/Button/Button'
import {parseKpiFieldValue} from '../helpers/seMetricsValidation'
import './globalSEMetricsTypography.css'

// ─── Validation ───────────────────────────────────────────────────────────────
const isValidPercentInput = (value: string): boolean => value === '' || TWO_DECIMAL_PATTERN.test(value)

// Range rule for `%` fields only. Empty and non-`%` units (e.g. Hrs) are exempt.
const validatePercentValue = (value: string, unit: string): string => {
  if (unit !== '%' || value === '' || value == null) return ''
  const parsed = parseFloat(value)
  if (isNaN(parsed)) return ''
  return parsed < GLOBAL_KPI_PERCENT_MIN || parsed > GLOBAL_KPI_PERCENT_MAX ? GLOBAL_KPI_PERCENT_RANGE_MESSAGE : ''
}

// ─── Main component ───────────────────────────────────────────────────────────

export interface UpdateKPIsSidePanelProps {
  isOpen: boolean
  initialData: BenchmarkMetadata | null
  onClose: () => void
  onSave: (data: BenchmarkMetadata) => Promise<void>
}

// All fields start empty so the counter's `0` shows as a faded placeholder,
const emptyForm = (): Record<string, any> => Object.fromEntries(GLOBAL_KPI_ORDERED_FIELDS.map(f => [f.key, '']))

const dataToForm = (data: BenchmarkMetadata | null): Record<string, any> => {
  const base = emptyForm()
  if (!data) return base
  GLOBAL_KPI_ORDERED_FIELDS.forEach(f => {
    const v = (data as any)[f.key]
    base[f.key] = v != null ? String(v) : ''
  })
  return base
}

const formToPayload = (form: Record<string, any>): BenchmarkMetadata => {
  const payload: BenchmarkMetadata = {}
  GLOBAL_KPI_ORDERED_FIELDS.forEach(f => {
    ;(payload as any)[f.key] = parseKpiFieldValue(form[f.key], f.type)
  })
  return payload
}

const UpdateKPIsSidePanel: React.FC<UpdateKPIsSidePanelProps> = ({isOpen, initialData, onClose, onSave}) => {
  const [form, setForm] = useState<Record<string, any>>(emptyForm)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (isOpen) setForm(dataToForm(initialData))
  }, [isOpen, initialData])

  const setField = (key: string, value: any) => setForm(prev => ({...prev, [key]: value}))

  // Range errors keyed by field, recomputed on every change.
  const errors = GLOBAL_KPI_ORDERED_FIELDS.reduce<Record<string, string>>((acc, field) => {
    if (field.type === GLOBAL_KPI_FIELD_TYPE.PERCENT) {
      const error = validatePercentValue(String(form[field.key] ?? ''), field.unit ?? '%')
      if (error) acc[field.key as string] = error
    }
    return acc
  }, {})
  const hasErrors = Object.keys(errors).length > 0

  const handleSave = async () => {
    if (hasErrors) return
    setSaving(true)
    try {
      await onSave(formToPayload(form))
    } finally {
      setSaving(false)
    }
  }

  return (
    <SidePanel
      title="Update KPIs"
      isOpen={isOpen}
      onClose={onClose}
      width="340px"
      showFooter={false}
      closeOnOutsideClick={false}
    >
      <div className="flex h-full flex-col">
        {/* Header description */}
        <p className="text-paragraph-default mb-4 px-1">Fill all the KPIs mentioned below for the Global benchmark.</p>

        {/* Scrollable fields */}
        <div className="flex-1 overflow-y-auto px-1">
          <div className="flex flex-col pb-4" style={{gap: '16px'}}>
            {GLOBAL_KPI_ORDERED_FIELDS.map(field =>
              field.type === GLOBAL_KPI_FIELD_TYPE.PERCENT ? (
                <KPIField
                  key={`${field.key}-${field.label}`}
                  type="unit"
                  name={field.key as string}
                  label={field.label}
                  value={String(form[field.key] ?? '')}
                  unit={field.unit ?? '%'}
                  error={errors[field.key as string]}
                  size="sm"
                  width="100%"
                  disabled={saving}
                  onChange={e => {
                    if (isValidPercentInput(e.target.value)) setField(field.key as string, e.target.value)
                  }}
                />
              ) : (
                <KPIField
                  key={`${field.key}-${field.label}`}
                  type="counter"
                  name={field.key as string}
                  label={field.label}
                  value={String(form[field.key] ?? '')}
                  size="sm"
                  width="100%"
                  disabled={saving}
                  onChange={e => setField(field.key as string, e.target.value)}
                />
              ),
            )}
          </div>
        </div>

        {/* Footer actions */}
        <div
          className="flex shrink-0 items-center justify-end border-t border-[var(--color-border-default)] px-1 pt-3"
          style={{gap: '16px'}}
        >
          <Button type="button" variant="tertiary" size="small" text="Cancel" onClick={onClose} disabled={saving} />
          <Button
            type="button"
            variant="primary"
            size="small"
            text={saving ? 'Saving…' : 'Save'}
            onClick={handleSave}
            disabled={saving || hasErrors}
          />
        </div>
      </div>
    </SidePanel>
  )
}

export default UpdateKPIsSidePanel
